import { useState, type MouseEvent } from "react";
import "./SkillRatingBar.css";

interface SkillRatingBarProps {
  skillName: string;
  rating: number;
  maxRating?: number;
  interactive?: boolean;
  onRatingChange?: (newRating: number) => void;
  showPercentage?: boolean;
}

const ratingLabels = [
  "No capability",
  "Basic proficiency",
  "Some experience",
  "Average expertise",
  "Advanced",
  "Expert",
];

export function SkillRatingBar({
  skillName,
  rating,
  maxRating = 5,
  interactive = false,
  onRatingChange,
  showPercentage = true,
}: SkillRatingBarProps) {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  const displayRating = hoveredRating !== null ? hoveredRating : rating;
  const displayPercentage = Math.round((displayRating / maxRating) * 100);

  const getLevelClass = (rating: number): string => {
    if (rating === 0) return "level-0";
    if (rating >= 4.5) return "level-5";
    if (rating >= 3.5) return "level-4";
    if (rating >= 2.5) return "level-3";
    if (rating >= 1.5) return "level-2";
    if (rating >= 0.5) return "level-1";
    return "level-0";
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!interactive || !onRatingChange) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newRating = Math.max(
      0,
      Math.min(maxRating, Math.ceil(percentage * maxRating)),
    );
    onRatingChange(newRating);
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const hoverRating = Math.max(
      0,
      Math.min(maxRating, Math.ceil(percentage * maxRating)),
    );
    setHoveredRating(hoverRating);
  };

  const handleMouseLeave = () => {
    setHoveredRating(null);
  };

  return (
    <div className="skill-rating-bar-container">
      <div className="skill-rating-bar-header">
        <div className="skill-rating-bar-name" title={skillName}>
          {skillName}
        </div>
        {showPercentage && (
          <div className="skill-rating-bar-percentage">
            {displayPercentage}%
          </div>
        )}
      </div>

      <div
        className={`skill-rating-bar-track ${interactive ? "skill-rating-bar-interactive" : ""}`}
        onClick={interactive ? handleClick : undefined}
        onMouseMove={interactive ? handleMouseMove : undefined}
        onMouseLeave={interactive ? handleMouseLeave : undefined}
      >
        <div
          className={`skill-rating-bar-fill ${getLevelClass(displayRating)}`}
          style={{ width: `${displayPercentage}%` }}
        >
          {displayRating > 0 && (
            <div className="skill-rating-bar-label">
              {displayRating}/{maxRating}
            </div>
          )}
        </div>

        {interactive && (
          <div className="skill-rating-bar-segments">
            {Array.from({ length: maxRating }).map((_, i) => (
              <div key={i} className="skill-rating-bar-segment" />
            ))}
          </div>
        )}

        {hoveredRating !== null && (
          <div className="skill-rating-bar-tooltip">
            {ratingLabels[hoveredRating]}
          </div>
        )}
      </div>
    </div>
  );
}
